

import { useState, useEffect, useRef } from "react"
import {
  initDB,
  getAllGroups,
  addGroup,
  updateGroupName,
  deleteGroup,
  getHistoryByGroup,
} from "./indexedDBService"
import { MathText, truncateText } from "./mathRenderer"
import StudyPage from "./StudyPage"

function GradedHistory({ gradedQuestions, onAddToGroup, setRefreshCallback, setSelectGroupCallback, forceRefresh }) {
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groupHistory, setGroupHistory] = useState([])
  const [isAddingGroup, setIsAddingGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState("")
  const [isEditingName, setIsEditingName] = useState(null)
  const [editedName, setEditedName] = useState("")
  const [isDBInitialized, setIsDBInitialized] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [expandedHistoryItems, setExpandedHistoryItems] = useState({})

  // New states for summary functionality
  const [showSummary, setShowSummary] = useState(false)
  const [summaryData, setSummaryData] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  // Reference to the summary content
  const summaryContentRef = useRef(null)


  const [showStudyPage, setShowStudyPage] = useState(false)

  useEffect(() => {
    if (isDBInitialized) {
      loadGroups()
    }
  }, [forceRefresh, isDBInitialized])
  // Initialize the database and load groups
  useEffect(() => {
    const initialize = async () => {
      try {
        await initDB()
        loadGroups()
        setIsDBInitialized(true)
      } catch (error) {
        console.error("Failed to initialize IndexedDB:", error)
      }
    }
    initialize()
  }, [])

  // Load all groups from IndexedDB
  const loadGroups = async () => {
    try {
      const allGroups = await getAllGroups()
      setGroups(allGroups)

      // Select the first group if none is selected
      if (allGroups.length > 0 && !selectedGroup) {
        setSelectedGroup(allGroups[0].id)
        loadGroupHistory(allGroups[0].id)
      } else if (selectedGroup) {
        // Refresh the current group's history
        loadGroupHistory(selectedGroup)
      }
    } catch (error) {
      console.error("Failed to load folders:", error)
    }
  }

  // Load history for a specific group
  const loadGroupHistory = async (groupId) => {
    try {
      const history = await getHistoryByGroup(groupId)
      setGroupHistory(history)
    } catch (error) {
      console.error(`Failed to load history for folder ${groupId}:`, error)
    }
  }

  // Handle group selection
  const handleSelectGroup = (groupId) => {
    setSelectedGroup(groupId)
    loadGroupHistory(groupId)
  }

  // Handle adding a new group
  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return

    try {
      const groupId = await addGroup(newGroupName.trim())
      setNewGroupName("")
      setIsAddingGroup(false)
      loadGroups()
    } catch (error) {
      console.error("Failed to add folder:", error)
    }
  }

  // Handle renaming a group
  const handleRenameGroup = async (groupId) => {
    if (!editedName.trim()) return

    try {
      await updateGroupName(groupId, editedName.trim())
      setIsEditingName(null)
      setEditedName("")
      loadGroups()
    } catch (error) {
      console.error("Failed to rename folder:", error)
    }
  }

  // Handle deleting a group
  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteGroup(groupId)
      if (selectedGroup === groupId) {
        setSelectedGroup(null)
        setGroupHistory([])
      }
      loadGroups()
      setShowDeleteConfirm(false)
      setGroupToDelete(null)
    } catch (error) {
      console.error("Failed to delete folder:", error)
    }
  }

  // Show delete confirmation dialog
  const confirmDeleteGroup = (groupId) => {
    setGroupToDelete(groupId)
    setShowDeleteConfirm(true)
  }

  // Start editing a group name
  const startEditingName = (group) => {
    setIsEditingName(group.id)
    setEditedName(group.name)
  }

  // Toggle expanded state for history items
  const toggleHistoryItemExpand = (id) => {
    setExpandedHistoryItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  // Generate summary for a folder
  const generateSummary = (groupId) => {
    setSummaryLoading(true)

    // Find the current folder
    const currentFolder = groups.find((g) => g.id === groupId)

    // Calculate summary data
    setTimeout(() => {
      const totalQuestions = groupHistory.length
      let totalScore = 0
      let totalMaxScore = 0

      // Calculate total scores
      groupHistory.forEach((q) => {
        totalScore += Number.parseFloat(q.grade || 0)
        totalMaxScore += Number.parseFloat(q.maxgrade || 0)
      })

      // Calculate average score as percentage
      const averagePercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0

      // Generate summary data
      const summary = {
        folderName: currentFolder?.name || "Unnamed Folder",
        totalQuestions,
        totalScore,
        totalMaxScore,
        averagePercentage,
        questions: groupHistory.map((q) => ({
          questionNumber: q.questionNumber,
          score: `${q.grade}/${q.maxgrade}`,
          scorePercentage:
            Number.parseFloat(q.maxgrade) > 0
              ? Math.round((Number.parseFloat(q.grade) / Number.parseFloat(q.maxgrade)) * 100)
              : 0,
          feedback: q.feedback,
          questionContent: q.questionContent,
          studentSolution: q.studentSolution,
        })),
      }

      setSummaryData(summary)
      setShowSummary(true)
      setSummaryLoading(false)
    }, 500) // Simulate processing time
  }

  // Download summary as text file
  const downloadSummary = (format = "txt") => {
    if (!summaryData) return

    if (format === "txt") {
      // Create text content
      let content = `# Summary for ${summaryData.folderName}\n\n`
      content += `Total Questions: ${summaryData.totalQuestions}\n`
      content += `Total Score: ${summaryData.totalScore}/${summaryData.totalMaxScore} (${summaryData.averagePercentage}%)\n\n`

      // Add details for each question
      summaryData.questions.forEach((q, index) => {
        content += `## Question ${q.questionNumber || index + 1}\n`
        content += `Score: ${q.score} (${q.scorePercentage}%)\n`
        content += `Question: ${q.questionContent}\n`
        content += `Student Solution: ${q.studentSolution}\n`
        content += `Feedback: ${q.feedback}\n\n`
      })

      // Create and trigger download
      const blob = new Blob([content], { type: "text/plain" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${summaryData.folderName.replace(/\s+/g, "_")}_Summary.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } else if (format === "pdf") {
      // For PDF, we'll use a simpler approach - print the modal content
      printSummary()
    }
  }

  // Print the summary modal content
  const printSummary = () => {
    if (!summaryData) return

    setIsPrinting(true)

    // We need to wait a bit for the printing class to be applied
    setTimeout(() => {
      window.print()

      // Remove the printing class after printing is done
      setTimeout(() => {
        setIsPrinting(false)
      }, 500)
    }, 100)
  }

  useEffect(() => {
    // Provide a callback function that the parent can call to refresh the current group
    if (setRefreshCallback) {
      setRefreshCallback((groupId) => {
        // If a specific group ID is provided, use that, otherwise use the currently selected group
        const targetGroupId = groupId || selectedGroup
        if (targetGroupId) {
          loadGroupHistory(targetGroupId)
        }
      })
    }

    // Clean up the callback when the component unmounts
    return () => {
      if (setRefreshCallback) {
        setRefreshCallback(null)
      }
    }
  }, [selectedGroup, setRefreshCallback])
  // Set up the callback for programmatically selecting a group
  useEffect(() => {
    if (setSelectGroupCallback) {
      setSelectGroupCallback((groupId) => {
        if (groupId) {
          setSelectedGroup(groupId)
          loadGroupHistory(groupId)
        }
      })
    }

    return () => {
      if (setSelectGroupCallback) {
        setSelectGroupCallback(null)
      }
    }
  }, [setSelectGroupCallback])
  return (
    <aside className="w-full md:w-80 lg:w-96 bg-gradient-to-br from-white to-blue-50 border-l border-gray-200 overflow-y-auto min-h-screen">
      <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-white z-10 border-b border-gray-100 px-4 py-3 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              History
            </h2>
            <p className="text-xs text-gray-500 ml-7">Organize and review graded questions</p>
          </div>
          <button
            onClick={() => setIsAddingGroup(true)}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium flex items-center bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Folder
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Add new folder form */}
        {isAddingGroup && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100 shadow-sm">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full p-2 mb-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsAddingGroup(false)}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAddGroup}
                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 focus:ring-2 focus:ring-blue-300"
              >
                Add Folder
              </button>
            </div>
          </div>
        )}

        {/* Folders list */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Exam Folders</h3>
          {groups.length > 0 ? (
            <ul className="space-y-1">
              {groups.map((group) => (
                <li key={group.id} className="relative group">
                  {isEditingName === group.id ? (
                    <div className="flex items-center p-2 bg-blue-50 rounded">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="flex-1 p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleRenameGroup(group.id)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button onClick={() => setIsEditingName(null)} className="ml-1 text-red-600 hover:text-red-800">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all duration-200 ${selectedGroup === group.id ? "bg-blue-100 text-blue-800" : "hover:bg-gray-100 text-gray-700"
                        }`}
                      onClick={() => handleSelectGroup(group.id)}
                    >
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 mr-2 ${selectedGroup === group.id ? "text-blue-500" : "text-gray-400"}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                          />
                        </svg>
                        <span className="text-sm font-medium">{group.name}</span>
                      </div>
                      <div className="flex opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            startEditingName(group)
                          }}
                          className="text-gray-500 hover:text-gray-700 ml-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            confirmDeleteGroup(group.id)
                          }}
                          className="text-gray-500 hover:text-red-700 ml-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 mx-auto text-gray-300 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
              <p className="text-gray-500 text-sm">No exam folders yet</p>
              <button
                onClick={() => setIsAddingGroup(true)}
                className="mt-2 text-blue-500 text-xs font-medium hover:text-blue-700"
              >
                Create your first folder
              </button>
            </div>
          )}
        </div>

        {/* Current folder history or ungrouped questions */}
        {selectedGroup ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {groups.find((g) => g.id === selectedGroup)?.name} History
              </h3>
              <div className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {groupHistory.length}
                </span>

                {/* Summary button */}
                {groupHistory.length > 0 && (
                  <button
                    onClick={() => generateSummary(selectedGroup)}
                    disabled={summaryLoading}
                    className="bg-green-100 hover:bg-green-200 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full flex items-center transition-colors"
                  >
                    {summaryLoading ? (
                      <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    )}
                    Summarize and Learn
                  </button>
                )}
              </div>
            </div>

            {groupHistory.length > 0 ? (
              <ul className="space-y-3">
                {groupHistory.map((q, index) => {
                  const itemId = `history-${q.id || index}`
                  const isExpanded = expandedHistoryItems[itemId]
                  return (
                    <li
                      key={itemId}
                      className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center text-green-600 font-medium text-sm">
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          Question {q.questionNumber || index + 1}
                        </div>
                        <span className="text-xs text-gray-500">{new Date(q.timestamp).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <span className="text-xs font-medium text-gray-500 mr-1">Score:</span>
                        <span className="text-sm font-semibold text-blue-600">
                          {q.grade}/{q.maxgrade}
                        </span>
                      </div>
                      <div className="mb-2">
                        <p className={`text-xs text-gray-600 ${isExpanded ? "" : "line-clamp-2"}`}>
                          {isExpanded ? q.feedback : truncateText(q.feedback, 100)}
                        </p>
                      </div>
                      <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 mt-1">
                        <div className={isExpanded ? "" : "truncate"}>
                          <span className="font-medium">Q:</span>{" "}
                          {isExpanded ? (
                            <MathText text={q.questionContent} className="math-content inline-block" />
                          ) : (
                            <MathText
                              text={truncateText(q.questionContent, 40)}
                              className="math-content inline-block"
                            />
                          )}
                        </div>
                        <div className={isExpanded ? "" : "truncate"}>
                          <span className="font-medium">A:</span>{" "}
                          {isExpanded ? (
                            <MathText text={q.studentSolution} className="math-content inline-block" />
                          ) : (
                            <MathText
                              text={truncateText(q.studentSolution, 40)}
                              className="math-content inline-block"
                            />
                          )}
                        </div>
                        <button
                          onClick={() => toggleHistoryItemExpand(itemId)}
                          className="mt-1 text-blue-500 hover:text-blue-700 text-xs font-medium flex items-center"
                        >
                          {isExpanded ? (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              Show Less
                            </>
                          ) : (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              Show All
                            </>
                          )}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              // Keep the existing empty state
              <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-100">
                <svg
                  className="w-10 h-10 mx-auto text-gray-300 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                  />
                </svg>
                <p className="text-gray-600 text-sm">No graded questions in this folder</p>
                <p className="text-gray-500 text-xs mt-1">Grade questions and add them to this folder</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-100">
            <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            <p className="text-gray-600 text-sm">Select a folder to view history</p>
            <p className="text-gray-500 text-xs mt-1">Or create a new folder to organize your graded questions</p>
          </div>
        )}

        {/* Recently graded questions section */}
        {gradedQuestions.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recently Graded</h3>
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {gradedQuestions.length}
              </span>
            </div>
            <ul className="space-y-3">
              {gradedQuestions.slice(0, 100).map((q, index) => {
                const itemId = `recent-${index}`
                const isExpanded = expandedHistoryItems[itemId]
                return (
                  <li
                    key={itemId}
                    className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center text-green-600 font-medium text-sm">
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Question {q.questionNumber || index + 1}
                      </div>
                      {isDBInitialized && groups.length > 0 && (
                        <button
                          className="text-blue-500 hover:text-blue-700 text-xs font-medium flex items-center"
                          onClick={() => onAddToGroup(q, selectedGroup)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add to Folder
                        </button>
                      )}
                    </div>
                    <div className="flex items-center mb-1">
                      <span className="text-xs font-medium text-gray-500 mr-1">Score:</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {q.grade}/{q.maxgrade}
                      </span>
                    </div>
                    <div className="mb-1">
                      <p className={`text-xs text-gray-600 ${isExpanded ? "" : "line-clamp-2"}`}>
                        {isExpanded ? q.feedback : truncateText(q.feedback, 100)}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 mt-1">
                      <div className={isExpanded ? "" : "truncate"}>
                        <span className="font-medium">Q:</span>{" "}
                        {isExpanded ? (
                          <MathText text={q.questionContent} className="math-content inline-block" />
                        ) : (
                          <MathText text={truncateText(q.questionContent, 40)} className="math-content inline-block" />
                        )}
                      </div>
                      <div className={isExpanded ? "" : "truncate"}>
                        <span className="font-medium">A:</span>{" "}
                        {isExpanded ? (
                          <MathText text={q.studentSolution} className="math-content inline-block" />
                        ) : (
                          <MathText text={truncateText(q.studentSolution, 40)} className="math-content inline-block" />
                        )}
                      </div>
                      <button
                        onClick={() => toggleHistoryItemExpand(itemId)}
                        className="mt-1 text-blue-500 hover:text-blue-700 text-xs font-medium flex items-center"
                      >
                        {isExpanded ? (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            Show Less
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            Show All
                          </>
                        )}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">Confirm Delete</h3>
            <p className="mb-5 text-gray-600 text-sm">
              Are you sure you want to delete this folder and all its history? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGroup(groupToDelete)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-medium text-sm focus:ring-2 focus:ring-red-300"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummary && summaryData && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isPrinting ? "printing" : ""}`}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
            ref={summaryContentRef}
          >
            <div className="p-5 border-b border-gray-200 print:border-b-2">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 print:text-2xl">
                  Summary for {summaryData.folderName}
                </h3>
                <button
                  onClick={() => setShowSummary(false)}
                  className="text-gray-400 hover:text-gray-600 print:hidden"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto flex-grow print:overflow-visible print:p-8">
              <div className="bg-blue-50 p-4 rounded-lg mb-4 print:bg-blue-50 print:border print:border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 print:text-base">Total Questions</p>
                    <p className="text-xl font-bold text-gray-800 print:text-2xl">{summaryData.totalQuestions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 print:text-base">Average Score</p>
                    <p className="text-xl font-bold text-gray-800 print:text-2xl">{summaryData.averagePercentage}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 print:text-base">Total Score</p>
                    <p className="text-xl font-bold text-blue-600 print:text-2xl">
                      {summaryData.totalScore}/{summaryData.totalMaxScore}
                    </p>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold text-gray-700 mb-3 print:text-xl print:mt-6">Question Details</h4>

              <div className="space-y-4 print:space-y-8">
                {summaryData.questions.map((q, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 print:p-4 print:border-2 print:page-break-inside-avoid"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-gray-800 print:text-lg">
                        Question {q.questionNumber || idx + 1}
                      </h5>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium print:text-sm print:py-1 print:px-3 ${q.scorePercentage >= 80
                          ? "bg-green-100 text-green-800 print:bg-green-100"
                          : q.scorePercentage >= 60
                            ? "bg-yellow-100 text-yellow-800 print:bg-yellow-100"
                            : "bg-red-100 text-red-800 print:bg-red-100"
                          }`}
                      >
                        {q.score} ({q.scorePercentage}%)
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 mb-2 print:text-base print:mb-4">
                      <p className="font-medium mb-1 print:mb-2">Question:</p>
                      <div className="pl-3 border-l-2 border-gray-200 print:pl-4">
                        <MathText text={q.questionContent} className="math-content print:text-base" />
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-2 print:text-base print:mb-4">
                      <p className="font-medium mb-1 print:mb-2">Student Solution:</p>
                      <div className="pl-3 border-l-2 border-gray-200 print:pl-4">
                        <MathText text={q.studentSolution} className="math-content print:text-base" />
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 print:text-base">
                      <p className="font-medium mb-1 print:mb-2">Feedback:</p>
                      <div className="pl-3 border-l-2 border-gray-200 print:pl-4">{q.feedback}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end print:hidden">
              <button
                onClick={() => setShowSummary(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium mr-2"
              >
                Close
              </button>
              <div className="flex">
                <button
                  onClick={() => downloadSummary("txt")}
                  className="px-4 py-2 bg-blue-500 text-white rounded-l-md hover:bg-blue-600 font-medium text-sm focus:ring-2 focus:ring-blue-300 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download TXT
                </button>
                <button
                  onClick={() => printSummary()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 font-medium text-sm focus:ring-2 focus:ring-blue-300 flex items-center border-l border-blue-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                    />
                  </svg>
                  Print PDF
                </button>
                <button
                  onClick={() => {
                    setShowSummary(false)
                    setShowStudyPage(true)
                  }}
                  className="px-4 py-2 mx-32 bg-gradient-to-r from-purple-800 to-red-500 text-white rounded-md hover:bg-green-600 font-medium text-sm focus:ring-2 focus:ring-green-300 flex items-center mr-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  Study for the Exam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showStudyPage && summaryData && <StudyPage summaryData={summaryData} onClose={() => setShowStudyPage(false)} />}

      {/* Print-specific styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .printing, .printing * {
            visibility: visible;
          }
          .printing {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .printing > div {
            width: 100%;
            max-width: 100%;
            box-shadow: none;
            border-radius: 0;
            height: auto;
            max-height: none;
          }
          .katex {
            font-size: 1.2em !important;
          }
          .math-content {
            font-size: 14pt !important;
          }
        }
      `}</style>
    </aside>
  )
}

export default GradedHistory
